/**
 * Central analytics event handler
 * Handles both click events and auto-fire events (HTMX swaps, success pages)
 * Pushes structured events to Google Tag Manager dataLayer
 */

export function initAnalytics() {
  /**
   * Extracts analytics data from data-analytics-* attributes
   * These attributes are typically populated by the {% analytics_attrs %} template tag
   * Converts camelCase attribute names to snake_case for GA4 convention
   */
  function extractAnalyticsData(element) {
    const analyticsData = {}
    for (const [key, value] of Object.entries(element.dataset)) {
      if (key.startsWith("analytics")) {
        const snakeKey = key
          .replace("analytics", "")
          .replace(/([A-Z])/g, "_$1")
          .toLowerCase()
          .slice(1)

        if (value === "true") {
          analyticsData[snakeKey] = true
        } else if (value === "false") {
          analyticsData[snakeKey] = false
        } else {
          analyticsData[snakeKey] = value
        }
      }
    }
    return analyticsData
  }

  /**
   * Fires an analytics event for auto-fire scenarios (HTMX swaps, page loads)
   * Automatically includes timestamp
   * Includes last CTA click if data-analytics-include-last-cta="true"
   */
  function fireAnalyticsEvent(element) {
    if (!window.dataLayer) return

    const analyticsData = extractAnalyticsData(element)
    analyticsData.timestamp = new Date().toISOString()

    // Add last CTA click if requested
    if (element.dataset.analyticsIncludeLastCta === "true") {
      // Remove the control flag from analytics data
      delete analyticsData.include_last_cta

      const lastCta = sessionStorage.getItem("last_cta")
      if (lastCta) {
        analyticsData.last_cta = lastCta
      }
    }

    window.dataLayer.push(analyticsData)
  }

  // Handler 1: Click events (CTAs, buttons, links)
  document.addEventListener("click", (e) => {
    const element = e.target.closest("[data-analytics-event]")

    if (element && window.dataLayer) {
      const analyticsData = extractAnalyticsData(element)
      analyticsData.timestamp = new Date().toISOString()

      window.dataLayer.push(analyticsData)

      // Store CTA clicks for attribution tracking
      if (analyticsData.event === "cta_click" && analyticsData.element_id) {
        sessionStorage.setItem("last_cta", analyticsData.element_id)
      }
    }
  })

  // Handler 2: Auto-fire events on initial page load
  // Initialize dataLayer if it doesn't exist (e.g., before GTM loads or consent given)
  window.dataLayer = window.dataLayer || []
  document
    .querySelectorAll("[data-analytics-event][data-analytics-autofire]")
    .forEach(fireAnalyticsEvent)

  // Handler 3: Auto-fire events when new HTMX content is loaded
  document.body.addEventListener("htmx:load", (event) => {
    const newContent = event.detail.elt

    // Find all analytics events with autofire in the newly loaded content
    const analyticsElements = newContent.querySelectorAll(
      "[data-analytics-event][data-analytics-autofire]",
    )
    analyticsElements.forEach(fireAnalyticsEvent)

    // Also check if the loaded element itself has analytics event and autofire
    if (
      newContent.hasAttribute("data-analytics-event") &&
      newContent.hasAttribute("data-analytics-autofire")
    ) {
      fireAnalyticsEvent(newContent)
    }
  })
}
